import React, { useMemo, useState ,useEffect, useRef} from "react";

/**
 * SalesHeatmap
 *
 * Renders a scrollable, color-coded heatmap of monthly sales per salesperson.
 *
 * Expected data shape (array of objects), matching typical SQL pivot output:
 * [
 *   {
 *     sman_key: "MANSHAD",
 *     sman_name: "MANSHAD",
 *     "2026-07": 13585.54,
 *     "2026-06": 195375.87,
 *     ...
 *     TotalSales: 657264.27,
 *     AvgMonthlySales: 54772.02
 *   },
 *   ...
 * ]
 *
 * Month keys are auto-detected from the data (any key matching YYYY-MM),
 * so you don't need to pass them separately. Missing/null months render
 * as blank cells rather than zero, since they usually mean "no data".
 */

const MONTH_KEY_REGEX = /^\d{4}-\d{2}$/;

// Sequential blue ramp: light (low sales) -> dark (high sales)
const COLOR_STOPS = [
  { t: 0, c: [230, 241, 251] },
  { t: 0.5, c: [133, 183, 235] },
  { t: 1, c: [12, 68, 124] },
];

function interpolateColor(t) {
  let lo = COLOR_STOPS[0];
  let hi = COLOR_STOPS[COLOR_STOPS.length - 1];
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    if (t >= COLOR_STOPS[i].t && t <= COLOR_STOPS[i + 1].t) {
      lo = COLOR_STOPS[i];
      hi = COLOR_STOPS[i + 1];
      break;
    }
  }
  const span = hi.t - lo.t || 1;
  const f = (t - lo.t) / span;
  return lo.c.map((c, i) => Math.round(c + (hi.c[i] - c) * f));
}

function cellStyleFor(value, min, max) {
  if (value === null || value === undefined) {
    return { background: "transparent", color: "#9CA3AF" };
  }
  const t = max === min ? 1 : (value - min) / (max - min);
  const [r, g, b] = interpolateColor(t);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return {
    background: `rgb(${r}, ${g}, ${b})`,
    color: brightness > 140 ? "#0C447C" : "#E6F1FB",
  };
}

function formatCurrency(value, compact = true) {
  if (value === null || value === undefined) return "–";
  if (compact) {
    return Math.round(value / 1000).toLocaleString() + "k";
  }
  return Math.round(value).toLocaleString();
}

export default function SalesmanMonthWise({ data = [], nameKey = "sman_name", maxHeight = 420 , onCellClick ,baseUrl, company_code_from_local_storage, selectedBranch}) {


  const [sortDesc, setSortDesc] = useState(true);
  const scrollContainerRef = useRef(null);

  // Auto-detect month columns from the first row, sorted chronologically
  const months = useMemo(() => {
    if (!data.length) return [];
    const keys = Object.keys(data[0]).filter((k) => MONTH_KEY_REGEX.test(k));
    return keys.sort();
  }, [data]);

  const rows = useMemo(() => {
    const mapped = data.map((row) => ({
      name: (row[nameKey] || "").trim(),
      rawRow: row,
      values: months.map((m) => (row[m] === undefined ? null : row[m])),
      total: row.TotalSales ?? months.reduce((sum, m) => sum + (row[m] || 0), 0),
      avg: row.AvgMonthlySales,
    }));
    return mapped.sort((a, b) => (sortDesc ? b.total - a.total : a.total - b.total));
  }, [data, months, nameKey, sortDesc]);

  const { min, max } = useMemo(() => {
    const allValues = rows.flatMap((r) => r.values).filter((v) => v !== null && v !== undefined);
    return {
      min: allValues.length ? Math.min(...allValues) : 0,
      max: allValues.length ? Math.max(...allValues) : 1,
    };
  }, [rows]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, [rows, months]);

  if (!data.length) {
    return <div style={{ fontSize: 14, color: "#6B7280" }}>No data to display.</div>;
  }

  const handleCellClick = async (row, monthStr, value) => {
    if (value === null || value === undefined) return; // Skip if no data
  
    // 1. Extract start and end dates from "YYYY-MM"
    const [year, month] = monthStr.split("-").map(Number);
    const startDate = `${monthStr}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${monthStr}-${String(lastDay).padStart(2, "0")}`;
    
    // 2. Get the salesman key from your data row (e.g., "CBM002")
    const smanKey = row.rawRow.sman_key || "MAIN"; 
  
    // 3. Construct the dynamic API URL
    const apiUrl = `${baseUrl}/api/CRMSalesReport/${company_code_from_local_storage}/SALESREPORT_ALL/${startDate}/${endDate}/${smanKey}/${selectedBranch}`;
  
    try {
      // 4. Fire the API call
      const response = await fetch(apiUrl);
      const resultData = await response.json(); // This yields your invoice array
      
      // 5. Send it to the parent component to render the detailed table
      if (onCellClick) {
        onCellClick({
          salesperson: row.name,
          month: monthStr,
          apiData: resultData // Your array of invoices
        });
      }
    } catch (error) {
      console.error("Error fetching breakdown records:", error);
    }
  };

  return (
    <div style={{ fontFamily: "sans-serif" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <button
          onClick={() => setSortDesc((v) => !v)}
          style={{
            fontSize: 12,
            padding: "6px 10px",
            border: "1px solid #D1D5DB",
            borderRadius: 6,
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Sort by total: {sortDesc ? "High → Low" : "Low → High"}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#6B7280" }}>
          <span>Low</span>
          <div
            style={{
              width: 80,
              height: 8,
              borderRadius: 4,
              background: "linear-gradient(to right, rgb(230,241,251), rgb(133,183,235), rgb(12,68,124))",
            }}
          />
          <span>High</span>
        </div>
      </div>

      <div
      ref={scrollContainerRef}
        style={{
          overflow: "auto",
          maxHeight,
          border: "1px solid #E5E7EB",
          borderRadius: 8,
        }}
      >
        <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 760 }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px 10px",
                  color: "#6B7280",
                  fontWeight: 500,
                  position: "sticky",
                  left: 0,
                  top: 0,
                  background: "#F9FAFB",
                  zIndex: 2,
                }}
              >
                Salesperson
              </th>
              {months.map((m) => (
                <th
                  key={m}
                  style={{
                    textAlign: "center",
                    padding: "8px 4px",
                    color: "#6B7280",
                    fontWeight: 500,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    position: "sticky",
                    top: 0,
                    background: "#F9FAFB",
                    zIndex: 1,
                  }}
                >
                  {m}
                </th>
              ))}
              <th
                style={{
                  textAlign: "right",
                  padding: "8px 10px",
                  color: "#6B7280",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  background: "#F9FAFB",
                  zIndex: 1,
                }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name}>
                <td
                  style={{
                    padding: "8px 10px",
                    fontWeight: 500,
                    position: "sticky",
                    left: 0,
                    background: "#fff",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.name}
                </td>
                {row.values.map((v, i) => {
                  const style = cellStyleFor(v, min, max);
                  return (
                    <td
                      key={months[i]}
                      title={v !== null ? formatCurrency(v, false) : "No data"}
                      onClick={() => handleCellClick(row, months[i], v)}

                      style={{
                        textAlign: "center",
                        padding: "8px 4px",
                        fontWeight: 500,
                        ...style,
                      }}
                    >
                      {formatCurrency(v)}
                    </td>
                  );
                })}
                <td
                  style={{
                    textAlign: "right",
                    padding: "8px 10px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatCurrency(row.total, false)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
